import { css, cx } from "styled-system/css";
import { format, startOfDay } from "date-fns";
import { es } from "date-fns/locale";
import { useCallback, useEffect, useState } from "react";
import type { PaymentStatus } from "@prisma/client";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { DashboardEmpty } from "@/components/dashboard/dashboard-empty";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { labelAppointmentStatus, labelPaymentStatus } from "@/lib/dashboard-labels";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AppointmentEditDialog,
  type AppointmentEditRow,
} from "@/components/dashboard/appointment-edit-dialog";
import { WhatsAppChatLink, CopyPhoneButton } from "@/components/dashboard/whatsapp-chat-link";

type Row = AppointmentEditRow & { paymentStatus: PaymentStatus };

function depositBadgeText(a: Row): string | null {
  if (a.depositStatus === "pending") return "Seña pendiente";
  if (a.depositStatus === "paid") return "Seña pagada";
  if (a.depositStatus === "waived") return "Seña condonada";
  if (a.service?.requiresDeposit) return "Sin seña";
  return null;
}

export function DashboardAppointments() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [list, setList] = useState<Row[] | null>(null);
  const [timezone, setTimezone] = useState("America/Argentina/Buenos_Aires");
  const [editing, setEditing] = useState<AppointmentEditRow | null>(null);
  const effectiveProfessionalId = sp.get("professionalId") ?? null;
  const allowEdit = effectiveProfessionalId == null;
  const listTab = sp.get("tab") === "history" ? "history" : "upcoming";

  const reload = useCallback(async () => {
    const qs = effectiveProfessionalId ? `?professionalId=${encodeURIComponent(effectiveProfessionalId)}` : "";
    const r = await fetchWithSessionSync(`/api/dashboard/appointments${qs}`, { credentials: "include" }, refresh);
    if (!r.ok) return;
    setList((await r.json()) as Row[]);
  }, [refresh, effectiveProfessionalId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const qs = effectiveProfessionalId ? `?professionalId=${encodeURIComponent(effectiveProfessionalId)}` : "";
      const [rSet, rList] = await Promise.all([
        fetchWithSessionSync(`/api/dashboard/settings${qs}`, { credentials: "include" }, refresh),
        fetchWithSessionSync(`/api/dashboard/appointments${qs}`, { credentials: "include" }, refresh),
      ]);
      if (cancelled) return;
      if (rSet.ok) {
        const s = (await rSet.json()) as { timezone: string };
        setTimezone(s.timezone);
      }
      if (rList.ok) setList((await rList.json()) as Row[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh, effectiveProfessionalId]);

  function goTab(next: "upcoming" | "history") {
    const base = "/dashboard/appointments";
    const pid = effectiveProfessionalId ? `professionalId=${encodeURIComponent(effectiveProfessionalId)}` : "";
    if (next === "history") {
      const q = [pid, "tab=history"].filter(Boolean).join("&");
      navigate(q ? `${base}?${q}` : `${base}?tab=history`);
    } else {
      navigate(pid ? `${base}?${pid}` : base);
    }
  }

  if (!list) {
    return (
      <DashboardPageShell eyebrow="Agenda" title="Turnos">
        <LoadingFrame variant="light" minHeight={300} />
      </DashboardPageShell>
    );
  }

  const today = startOfDay(new Date());
  const upcoming = list
    .filter((a) => new Date(a.startTime) >= today && a.status !== "CANCELLED")
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  const rest = list
    .filter((a) => !(new Date(a.startTime) >= today && a.status !== "CANCELLED"))
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

  return (
    <DashboardPageShell
      eyebrow="Agenda"
      title="Turnos"
      description="Próximos e historial. Editá datos del cliente, estado o reagendá respetando tu disponibilidad."
    >
      <AppointmentEditDialog
        appt={editing}
        timezone={timezone}
        open={editing != null}
        onClose={() => setEditing(null)}
        onSaved={() => void reload()}
      />
      {list.length === 0 ? (
        <DashboardEmpty
          title="Todavía no tenés turnos"
          hint="Compartí tu página de reservas con clientes. Los turnos aparecerán acá automáticamente."
        />
      ) : (
        <>
          <div className={css({ display: "flex", gap: "2", mb: "6", flexWrap: "wrap" })}>
            <button
              type="button"
              onClick={() => goTab("upcoming")}
              className={css({
                px: "4",
                py: "2",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "semibold",
                border: "none",
                cursor: "pointer",
                bg: listTab === "upcoming" ? "rgba(99, 102, 241, 0.15)" : "#f1f5f9",
                color: listTab === "upcoming" ? "token(colors.brand.700)" : "#64748b",
              })}
            >
              Próximas
            </button>
            <button
              type="button"
              onClick={() => goTab("history")}
              className={css({
                px: "4",
                py: "2",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "semibold",
                border: "none",
                cursor: "pointer",
                bg: listTab === "history" ? "rgba(99, 102, 241, 0.15)" : "#f1f5f9",
                color: listTab === "history" ? "token(colors.brand.700)" : "#64748b",
              })}
            >
              Historial
            </button>
          </div>
          {listTab === "upcoming" ? (
            <section>
              {upcoming.length === 0 ? (
                <p className={css({ fontSize: "sm", color: "#64748b" })}>No hay turnos futuros en la lista.</p>
              ) : (
                <ul className={css({ display: "flex", flexDir: "column", gap: "3" })}>
                  {upcoming.map((a) => (
                    <AppointmentRow key={a.id} a={a} onEdit={() => setEditing(a)} allowEdit={allowEdit} />
                  ))}
                </ul>
              )}
            </section>
          ) : (
            <section>
              {rest.length === 0 ? (
                <p className={css({ fontSize: "sm", color: "#64748b" })}>No hay turnos en el historial.</p>
              ) : (
                <ul className={css({ display: "flex", flexDir: "column", gap: "3" })}>
                  {rest.map((a) => (
                    <AppointmentRow key={a.id} a={a} onEdit={() => setEditing(a)} allowEdit={allowEdit} />
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}
    </DashboardPageShell>
  );
}

function AppointmentRow({ a, onEdit, allowEdit }: { a: Row; onEdit: () => void; allowEdit: boolean }) {
  const start = new Date(a.startTime);
  const canEdit = allowEdit && a.status !== "CANCELLED";
  const dep = depositBadgeText(a);
  return (
    <li
      className={cx(
        cf.dashSurface,
        css({
          p: "4",
          display: "grid",
          gridTemplateColumns: { base: "1fr", md: "1fr auto auto auto" },
          gap: "3",
          alignItems: { md: "start" },
        }),
      )}
    >
      <div>
        <p className={css({ fontWeight: "semibold", color: "#0f172a" })}>{a.service?.name ?? a.court?.name ?? "—"}</p>
        <p className={css({ fontSize: "sm", color: "#64748b", mt: "1" })}>
          {format(start, "EEEE d MMM · HH:mm", { locale: es })}
        </p>
        <p className={css({ fontSize: "sm", color: "#64748b", mt: "1" })}>
          {a.clientName} · {a.clientEmail}
        </p>
        {a.clientPhone ? (
          <p className={css({ fontSize: "sm", color: "#64748b", mt: "0.5" })}>{a.clientPhone}</p>
        ) : null}
        {a.whatsappUnavailable ? (
          <span
            className={css({
              display: "inline-flex",
              alignItems: "center",
              gap: "1",
              mt: "2",
              px: "2",
              py: "1",
              rounded: "md",
              fontSize: "xs",
              fontWeight: "semibold",
              bg: "#fef2f2",
              color: "#dc2626",
              borderWidth: "1px",
              borderColor: "#fecaca",
            })}
            title="Un envío de WhatsApp a este número falló: no tiene WhatsApp"
          >
            ⚠️ Sin WhatsApp
          </span>
        ) : null}
      </div>
      <div className={css({ fontSize: "xs", color: "#475569" })}>
        <span className={css({ opacity: 0.65 })}>Estado</span> {labelAppointmentStatus(a.status)}
        {dep ? (
          <span
            className={css({
              display: "block",
              mt: "2",
              px: "2",
              py: "1",
              rounded: "md",
              fontWeight: "semibold",
              w: "fit-content",
              ...(dep === "Seña pendiente"
                ? { bg: "#fffbeb", color: "#b45309", borderWidth: "1px", borderColor: "#fcd34d" }
                : dep === "Seña pagada"
                  ? { bg: "#ecfdf5", color: "#166534", borderWidth: "1px", borderColor: "#bbf7d0" }
                  : { bg: "#f1f5f9", color: "#64748b", borderWidth: "1px", borderColor: "#e2e8f0" }),
            })}
          >
            {dep}
          </span>
        ) : null}
      </div>
      <div className={css({ fontSize: "xs", color: "#475569" })}>
        <span className={css({ opacity: 0.65 })}>Pago</span> {labelPaymentStatus(a.paymentStatus)}
      </div>
      <div className={css({ display: "flex", gap: "2", alignItems: "flex-start", flexWrap: "wrap", justifyContent: { base: "flex-start", md: "flex-end" } })}>
        <WhatsAppChatLink phone={a.clientPhone} />
        <CopyPhoneButton phone={a.clientPhone} />
        {canEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className={css({
              px: "4",
              py: "2",
              rounded: "lg",
              fontSize: "sm",
              fontWeight: "semibold",
              color: "#3730a3",
              borderWidth: "1px",
              borderColor: "#c7d2fe",
              bg: "#eef2ff",
              cursor: "pointer",
              _hover: { bg: "#e0e7ff", borderColor: "#a5b4fc" },
            })}
          >
            Editar / reagendar
          </button>
        ) : (
          <span className={css({ fontSize: "xs", color: "#94a3b8" })}>Cancelado</span>
        )}
      </div>
    </li>
  );
}
